class WhatsAppService {
  private static instance: WhatsAppService;
  private websockets: Map<string, WebSocket> = new Map();
  private qrCodeCallbacks: Map<string, ((qr: string) => void) | null> = new Map();
  private readyCallbacks: Map<string, (() => void) | null> = new Map();
  private messageCallbacks: Map<string, ((message: any) => void) | null> = new Map();
  private phoneNumberCallbacks: Map<string, ((phone: string) => void) | null> = new Map();
  private isAuthenticated: Map<string, boolean> = new Map();
  private conversations: Map<string, Map<string, any>> = new Map();
  private connectionAttempted: Map<string, boolean> = new Map();
  private reconnectTimeouts: Map<string, NodeJS.Timeout | null> = new Map();
  private reconnectAttempts: Map<string, number> = new Map();
  private maxReconnectAttempts: number = 5;
  private lastSyncTimestamp: Map<string, number> = new Map();

  private constructor() {}

  private setupWebSocket(companyId: string) {
    const ws = this.websockets.get(companyId);
    if (ws?.readyState === WebSocket.OPEN) {
      return;
    }

    if (ws) {
      ws.close();
      this.websockets.delete(companyId);
    }

    const wsUrl = this.getWebSocketUrl();
    console.log(`Conectando ao WebSocket para empresa ${companyId}:`, wsUrl);
    
    const newWs = new WebSocket(wsUrl);
    this.websockets.set(companyId, newWs);

    newWs.onopen = () => {
      console.log(`WebSocket conectado com sucesso para empresa ${companyId}`);
      this.reconnectAttempts.set(companyId, 0);
      const timeout = this.reconnectTimeouts.get(companyId);
      if (timeout) {
        clearTimeout(timeout);
        this.reconnectTimeouts.set(companyId, null);
      }
      
      // Send company ID as soon as connection is established
      newWs.send(JSON.stringify({
        action: 'register',
        companyId: companyId
      }));
    };

    newWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log(`Mensagem WebSocket recebida para empresa ${companyId}:`, data);
        
        switch (data.event) {
          case 'qr':
            if (data.data.qrCode) {
              console.log(`QR Code recebido para empresa ${companyId}`);
              this.isAuthenticated.set(companyId, false);
              const qrCallback = this.qrCodeCallbacks.get(companyId);
              if (qrCallback) {
                qrCallback(data.data.qrCode);
              }
            }
            break;

          case 'ready':
          case 'authenticated':
            console.log(`WhatsApp pronto/autenticado para empresa ${companyId}`);
            this.isAuthenticated.set(companyId, true);
            const qrCb = this.qrCodeCallbacks.get(companyId);
            if (qrCb) {
              qrCb("");
            }
            const readyCb = this.readyCallbacks.get(companyId);
            if (readyCb) {
              readyCb();
            }
            if (data.data.phoneNumber) {
              const phoneCb = this.phoneNumberCallbacks.get(companyId);
              if (phoneCb) {
                phoneCb(data.data.phoneNumber);
              }
            }
            break;

          case 'status':
            if (data.data.qrCode) {
              console.log(`Recebido QR Code do status para empresa ${companyId}`);
              this.isAuthenticated.set(companyId, false);
              const qrCodeCb = this.qrCodeCallbacks.get(companyId);
              if (qrCodeCb) {
                qrCodeCb(data.data.qrCode);
              }
            } else if (data.data.connected) {
              console.log(`Status: WhatsApp conectado para empresa ${companyId}`);
              this.isAuthenticated.set(companyId, true);
              const qrCallback = this.qrCodeCallbacks.get(companyId);
              if (qrCallback) {
                qrCallback("");
              }
              const readyCallback = this.readyCallbacks.get(companyId);
              if (readyCallback) {
                readyCallback();
              }
              if (data.data.phoneNumber) {
                const phoneCallback = this.phoneNumberCallbacks.get(companyId);
                if (phoneCallback) {
                  phoneCallback(data.data.phoneNumber);
                }
              }
            } else {
              console.log(`Status: WhatsApp desconectado para empresa ${companyId}`);
              this.isAuthenticated.set(companyId, false);
            }
            break;

          case 'message':
            const messageCb = this.messageCallbacks.get(companyId);
            if (messageCb) {
              const { from, body, notifyName, timestamp, fromMe } = data.data;
              const chatId = from;
              
              if (!this.conversations.has(companyId)) {
                this.conversations.set(companyId, new Map());
              }
              
              const companyConversations = this.conversations.get(companyId)!;
              
              const existingChat = companyConversations.get(chatId) || {
                id: chatId,
                name: notifyName || from,
                lastMessage: body,
                timestamp: timestamp || new Date().toISOString(),
                source: 'whatsapp' as const,
                messages: []
              };

              const messageExists = existingChat.messages.some(
                (m: any) => m.text === body && 
                           m.timestamp === new Date(timestamp || Date.now()).toLocaleTimeString()
              );

              if (!messageExists) {
                const newMessage = {
                  id: Date.now(),
                  sender: fromMe ? 'ai' : 'user',
                  text: body,
                  timestamp: new Date(timestamp || Date.now()).toLocaleTimeString()
                };

                existingChat.messages.push(newMessage);
                existingChat.lastMessage = body;
                existingChat.timestamp = timestamp || new Date().toISOString();
                
                companyConversations.set(chatId, existingChat);
                messageCb(data.data);
              }
            }
            break;

          case 'error':
            console.error(`Erro recebido para empresa ${companyId}:`, data.message);
            break;
        }
      } catch (error) {
        console.error(`Erro ao processar mensagem para empresa ${companyId}:`, error);
      }
    };

    newWs.onerror = (error) => {
      console.error(`Erro na conexão WebSocket para empresa ${companyId}:`, error);
      this.handleDisconnect(companyId);
    };

    newWs.onclose = () => {
      console.log(`Conexão WebSocket fechada para empresa ${companyId}`);
      this.websockets.delete(companyId);
      this.handleDisconnect(companyId);
    };
  }

  private async syncHistoricalMessages(companyId: string) {
    try {
      console.log(`Iniciando sincronização de conversas para empresa ${companyId}...`);
      const url = `${this.getApiUrl()}/api/whatsapp/messages?companyId=${companyId}`;
      console.log('URL de sincronização:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include' // Importante para enviar cookies de autenticação
      });

      if (!response.ok) {
        console.error('Resposta não ok:', response.status, response.statusText);
        throw new Error(`Falha ao sincronizar conversas: ${response.statusText}`);
      }

      const history = await response.json();
      console.log(`Histórico recebido para empresa ${companyId}:`, history);
      
      if (!this.conversations.has(companyId)) {
        this.conversations.set(companyId, new Map());
      }
      
      const companyConversations = this.conversations.get(companyId)!;
      
      if (Array.isArray(history)) {
        history.forEach((chat: any) => {
          if (!companyConversations.has(chat.chatId)) {
            companyConversations.set(chat.chatId, {
              id: chat.chatId,
              name: chat.messages[0]?.notifyName || chat.chatId,
              lastMessage: chat.messages[chat.messages.length - 1]?.body || '',
              timestamp: chat.messages[chat.messages.length - 1]?.timestamp || new Date().toISOString(),
              source: 'whatsapp' as const,
              messages: chat.messages.map((msg: any) => ({
                id: msg.id || Date.now(),
                sender: msg.fromMe ? 'ai' : 'user',
                text: msg.body,
                timestamp: new Date(msg.timestamp).toLocaleTimeString()
              }))
            });
          }
        });
      } else {
        console.error(`Resposta inválida do servidor para empresa ${companyId}:`, history);
        throw new Error('Formato de resposta inválido do servidor');
      }

      this.lastSyncTimestamp.set(companyId, Date.now());
      return true;
    } catch (error) {
      console.error(`Erro ao sincronizar conversas para empresa ${companyId}:`, error);
      throw error;
    }
  }

  private getWebSocketUrl(): string {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const apiDomain = process.env.NODE_ENV === 'production'
      ? 'api.agenteia.anoteja.com.br'
      : 'localhost:8080';
    return `${wsProtocol}//${apiDomain}`;
  }

  private getApiUrl(): string {
    const protocol = window.location.protocol;
    const apiDomain = process.env.NODE_ENV === 'production'
      ? 'api.agenteia.anoteja.com.br'
      : 'localhost:8080';
    console.log('API URL:', `${protocol}//${apiDomain}`);
    return `${protocol}//${apiDomain}`;
  }

  private handleDisconnect(companyId: string) {
    if (!this.isAuthenticated.get(companyId)) return;
    
    const attempts = this.reconnectAttempts.get(companyId) || 0;
    if (attempts < this.maxReconnectAttempts) {
      const timeout = this.reconnectTimeouts.get(companyId);
      if (!timeout) {
        const newTimeout = setTimeout(() => {
          console.log(`Tentando reconectar WebSocket para empresa ${companyId}...`);
          this.reconnectAttempts.set(companyId, (attempts + 1));
          this.setupWebSocket(companyId);
        }, 5000);
        this.reconnectTimeouts.set(companyId, newTimeout);
      }
    } else {
      console.log(`Máximo de tentativas de reconexão atingido para empresa ${companyId}`);
    }
  }

  public async checkStatus(companyId: string): Promise<{ connected: boolean; qrCode: string | null; phoneNumber: string | null }> {
    try {
      const response = await fetch(`${this.getApiUrl()}/whatsapp-status?companyId=${companyId}`);
      const status = await response.json();
      return {
        connected: status.connected,
        qrCode: status.qrCode,
        phoneNumber: status.phoneNumber
      };
    } catch (error) {
      console.error(`Erro ao verificar status para empresa ${companyId}:`, error);
      return { connected: false, qrCode: null, phoneNumber: null };
    }
  }

  public async syncConversations(companyId: string): Promise<boolean> {
    try {
      console.log(`Iniciando sincronização via método público para empresa ${companyId}`);
      const result = await this.syncHistoricalMessages(companyId);
      return result;
    } catch (error) {
      console.error(`Erro ao sincronizar conversas (método público) para empresa ${companyId}:`, error);
      throw error;
    }
  }

  public getConversations(companyId: string): Array<{
    id: string;
    name: string;
    lastMessage: string;
    timestamp: string;
    source: "whatsapp";
    messages: Array<{
      id: number;
      sender: "user" | "ai";
      text: string;
      timestamp: string;
    }>;
  }> {
    const companyConversations = this.conversations.get(companyId);
    if (!companyConversations) {
      return [];
    }
    return Array.from(companyConversations.values());
  }

  public async sendMessage(companyId: string, chatId: string, message: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.getApiUrl()}/send-whatsapp-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyId: companyId,
          to: chatId,
          message: message
        })
      });

      if (!response.ok) {
        throw new Error('Falha ao enviar mensagem');
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error(`Erro ao enviar mensagem para empresa ${companyId}:`, error);
      return false;
    }
  }

  public async sendAttachment(companyId: string, chatId: string, attachment: any): Promise<boolean> {
    try {
      const isLocalUrl = attachment.url.startsWith('blob:') || attachment.url.startsWith('data:');
      
      if (isLocalUrl) {
        console.log(`URL local detectada para anexo, enviando apenas notificação: ${attachment.type}`);
        
        const messageText = attachment.type === 'image' 
          ? '🖼️ [Imagem enviada localmente]' 
          : attachment.type === 'audio'
          ? '🎤 [Áudio enviado localmente]'
          : `📎 [Arquivo enviado localmente: ${attachment.name || 'documento'}]`;
        
        await this.sendMessage(companyId, chatId, messageText);
        return true;
      }
      
      const response = await fetch(`${this.getApiUrl()}/send-whatsapp-attachment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyId,
          to: chatId,
          attachmentUrl: attachment.url,
          attachmentType: attachment.type,
          attachmentName: attachment.name || '',
          attachmentMimeType: attachment.mimeType || ''
        })
      });

      if (!response.ok) {
        console.error('Resposta não ok ao enviar anexo:', response.status, response.statusText);
        throw new Error('Falha ao enviar anexo');
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error(`Erro ao enviar anexo para empresa ${companyId}:`, error);
      
      try {
        const messageText = attachment.type === 'image' 
          ? `🖼️ Imagem: ${attachment.url}` 
          : attachment.type === 'audio'
          ? `🎤 Áudio: ${attachment.url}`
          : `📎 ${attachment.name || 'Documento'}: ${attachment.url}`;
        
        await this.sendMessage(companyId, chatId, messageText);
      } catch (fallbackError) {
        console.error('Falha também no fallback:', fallbackError);
      }
      
      return false;
    }
  }

  public async initialize(
    companyId: string,
    onQRCode: (qr: string) => void,
    onReady: () => void,
    onMessage: (message: any) => void,
    onPhoneNumber: (phone: string) => void
  ) {
    if (this.connectionAttempted.get(companyId)) {
      console.log(`Conexão já foi tentada para empresa ${companyId}, desconectando primeiro...`);
      await this.disconnect(companyId);
    }

    this.qrCodeCallbacks.set(companyId, onQRCode);
    this.readyCallbacks.set(companyId, onReady);
    this.messageCallbacks.set(companyId, onMessage);
    this.phoneNumberCallbacks.set(companyId, onPhoneNumber);

    try {
      this.setupWebSocket(companyId);
      
      console.log(`Iniciando conexão com servidor WhatsApp para empresa ${companyId}...`);
      const response = await fetch(`${this.getApiUrl()}/connect-whatsapp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyId: companyId
        })
      });

      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Falha ao iniciar conexão com WhatsApp');
      }

      if (data.status?.qrCode) {
        onQRCode(data.status.qrCode);
      }

      if (data.status?.connected) {
        this.isAuthenticated.set(companyId, true);
        onReady();
      }

      if (data.status?.phoneNumber) {
        onPhoneNumber(data.status.phoneNumber);
      }

      console.log(`Conexão iniciada com sucesso para empresa ${companyId}`);
      this.connectionAttempted.set(companyId, true);
    } catch (error) {
      console.error(`Erro ao inicializar WhatsApp para empresa ${companyId}:`, error);
      this.connectionAttempted.set(companyId, false);
      throw error;
    }
  }

  public async disconnect(companyId: string) {
    try {
      const ws = this.websockets.get(companyId);
      if (ws) {
        ws.close();
        this.websockets.delete(companyId);
      }
      
      const timeout = this.reconnectTimeouts.get(companyId);
      if (timeout) {
        clearTimeout(timeout);
        this.reconnectTimeouts.set(companyId, null);
      }

      const response = await fetch(`${this.getApiUrl()}/disconnect-whatsapp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyId: companyId
        })
      });

      if (!response.ok) {
        throw new Error('Falha ao desconectar WhatsApp');
      }
      
      this.isAuthenticated.set(companyId, false);
      this.connectionAttempted.set(companyId, false);
      
      if (this.conversations.has(companyId)) {
        this.conversations.get(companyId)!.clear();
      }
      
      this.reconnectAttempts.set(companyId, 0);
      this.lastSyncTimestamp.delete(companyId);
    } catch (error) {
      console.error(`Erro ao desconectar para empresa ${companyId}:`, error);
      throw error;
    }
  }

  public onMessage(companyId: string, callback: (message: any) => void) {
    this.messageCallbacks.set(companyId, callback);
  }

  public offMessage(companyId: string, callback: (message: any) => void) {
    const current = this.messageCallbacks.get(companyId);
    if (current === callback) {
      this.messageCallbacks.delete(companyId);
    }
  }

  public static getInstance(): WhatsAppService {
    if (!WhatsAppService.instance) {
      WhatsAppService.instance = new WhatsAppService();
    }
    return WhatsAppService.instance;
  }
}

export const whatsappService = WhatsAppService.getInstance();
